/**
 * ReconciliationPanel - nightly ledger reconciliation
 *
 * lp-api checks the signing server's pool ledger against the Hedera mirror
 * node every night at 02:00 UTC. This shows the latest run, its failing and
 * warning checks, and lets an admin run it now.
 *
 * Styles are inline: the admin page loads this pack's JS but not its CSS.
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { getRequest, postRequest } from "../../../utils/httpRequest";
import { colors } from "../../AgenticPaymentTransactionHistory/styles";
import { API_ENDPOINTS } from "../constants";
import { formatNumber } from "../utils";
import { HelpTitle } from "../../AdminHeaderHelp";

/** Plain-language label and tooltip per check. The code stays as lp-api sends it. */
export const CHECK_INFO = {
    supply_matches_chain: {
        label: "Total supply matches Hedera",
        tip: "The ledger's total supply for this token is the same as on Hedera.",
    },
    circulating_matches_chain: {
        label: "Customer balance matches Hedera",
        tip: "What customers hold, in the ledger, equals the Hedera total minus what the treasury and our own wallets hold.",
    },
    treasury_holds_only_dust: {
        label: "Tokens left in treasury",
        tip: "The treasury should keep only 0.001 of each token. Anything above that was minted but never reached a customer, and needs to be burned.",
    },
    rate_fresh: {
        label: "Exchange rate is current",
        tip: "This currency has pool money, so its rate must be less than 24 hours old.",
    },
    usdb_backed: {
        label: "USDb is fully backed",
        tip: "The USDC we hold covers every USDb in existence.",
    },
    chain_readable: {
        label: "Token found on Hedera",
        tip: "We could read this token from Hedera.",
    },
    ledger_chain_synced: {
        label: "Ledger uses live data",
        tip: "The ledger showed live Hedera figures for this token, not saved ones.",
    },
};

const STATUS_COLORS = {
    pass: { bg: "#d4edda", fg: "#155724" },
    warn: { bg: "#fff3cd", fg: "#856404" },
    fail: { bg: "#f8d7da", fg: "#721c24" },
    error: { bg: "#f8d7da", fg: "#721c24" },
    running: { bg: "#d1ecf1", fg: "#0c5460" },
};

// A manual run takes about a minute; wait this long for it before giving up.
const POLL_EVERY_MS = 5000;
const POLL_FOR_MS = 3 * 60 * 1000;

const cardStyle = {
    background: colors.white,
    border: `1px solid ${colors.border}`,
    borderRadius: "8px",
    padding: "20px 24px",
    marginBottom: "20px",
};
const thStyle = { textAlign: "left", padding: "4px 8px", fontSize: "11px", color: colors.gray600, borderBottom: `1px solid ${colors.border}`, whiteSpace: "nowrap" };
const tdStyle = { padding: "4px 8px", fontSize: "12px", borderBottom: `1px solid ${colors.border}`, verticalAlign: "top" };
const linkButton = { background: "none", border: 0, color: colors.primary, cursor: "pointer", padding: 0, fontSize: "12px" };
// Warnings show this many rows when first expanded.
const WARN_PREVIEW = 10;
const STATUS_ORDER = { fail: 0, warn: 1, pass: 2 };
const buttonStyle = (disabled) => ({
    background: disabled ? "#9a93b8" : colors.primary,
    color: "#fff",
    border: 0,
    borderRadius: "6px",
    padding: "8px 16px",
    fontWeight: 600,
    cursor: disabled ? "not-allowed" : "pointer",
});

const Badge = ({ status }) => {
    const c = STATUS_COLORS[status] || { bg: "#e2e3e5", fg: "#383d41" };
    return (
        <span style={{ background: c.bg, color: c.fg, borderRadius: "10px", padding: "2px 10px", fontSize: "12px", fontWeight: 700, textTransform: "uppercase" }}>
            {status}
        </span>
    );
};

const formatTime = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

const difference = (expected, actual) => {
    const e = Number(expected);
    const a = Number(actual);
    return Number.isFinite(e) && Number.isFinite(a) ? a - e : null;
};

const value = (v) => (typeof v === "number" ? formatNumber(v) : v == null ? "-" : String(v));

const offBy = (check) => Math.abs(difference(check.expected, check.actual) || 0);

const CheckRow = ({ check }) => {
    const [open, setOpen] = useState(false);
    const diff = difference(check.expected, check.actual);
    return (
        <>
            <tr>
                <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                    <strong>{check.symbol || "-"}</strong>
                    {check.tokenId && <span style={{ marginLeft: "6px", color: colors.gray600 }}>{check.tokenId}</span>}
                </td>
                <td style={tdStyle}>{value(check.expected)}</td>
                <td style={tdStyle}>{value(check.actual)}</td>
                <td style={{ ...tdStyle, color: diff ? "#721c24" : undefined }}>{diff == null ? "-" : formatNumber(diff)}</td>
                <td style={tdStyle}>
                    <button type="button" onClick={() => setOpen(!open)} style={linkButton} aria-expanded={open}>
                        {open ? "Hide" : "Details"}
                    </button>
                </td>
            </tr>
            {open && (
                <tr>
                    <td style={tdStyle} colSpan={5}>
                        <dl style={{ display: "grid", gridTemplateColumns: "max-content 1fr", gap: "2px 16px", margin: 0 }}>
                            <dt style={{ color: colors.gray600, fontWeight: 400 }}>check</dt>
                            <dd style={{ margin: 0 }}><code style={{ fontSize: "11px" }}>{check.check}</code></dd>
                            {Object.entries(check.details || {}).map(([k, v]) => (
                                <React.Fragment key={k}>
                                    <dt style={{ color: colors.gray600, fontWeight: 400 }}>{k}</dt>
                                    <dd style={{ margin: 0 }}>{typeof v === "object" ? JSON.stringify(v) : value(v)}</dd>
                                </React.Fragment>
                            ))}
                        </dl>
                    </td>
                </tr>
            )}
        </>
    );
};

const RowsTable = ({ rows }) => (
    <table style={{ width: "100%", borderCollapse: "collapse", margin: "4px 0 8px" }}>
        <thead>
            <tr>
                <th style={thStyle}>Token</th>
                <th style={thStyle}>Should be</th>
                <th style={thStyle}>Is</th>
                <th style={thStyle}><HelpTitle title="Off by" help="Is minus should be." /></th>
                <th style={thStyle} />
            </tr>
        </thead>
        <tbody>
            {rows.map((c, i) => (
                <CheckRow key={`${c.check}-${c.tokenId || c.symbol || i}`} check={c} />
            ))}
        </tbody>
    </table>
);

/** One check type: its counts, then its failing rows, or its warnings folded away. */
const CheckGroup = ({ code, counts, rows }) => {
    const info = CHECK_INFO[code];
    const label = info?.label || code;
    const fails = rows.filter((r) => r.status === "fail").sort((a, b) => offBy(b) - offBy(a));
    const warns = rows.filter((r) => r.status === "warn").sort((a, b) => offBy(b) - offBy(a));
    const [showWarns, setShowWarns] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [filter, setFilter] = useState("");

    const total = (counts.pass || 0) + (counts.warn || 0) + (counts.fail || 0);
    if (!counts.fail && !counts.warn) {
        return (
            <div style={{ padding: "6px 0", fontSize: "13px", borderBottom: `1px solid ${colors.border}` }} title={info?.tip || ""}>
                <span style={{ color: "#155724", fontWeight: 700 }}>✓</span> {label}: <span style={{ color: "#155724" }}>All {total} passed</span>
            </div>
        );
    }

    const term = filter.trim().toLowerCase();
    const filteredWarns = term
        ? warns.filter((r) => `${r.symbol || ""} ${r.tokenId || ""}`.toLowerCase().includes(term))
        : warns;
    const shownWarns = showAll || term ? filteredWarns : filteredWarns.slice(0, WARN_PREVIEW);

    return (
        <div style={{ padding: "6px 0", borderBottom: `1px solid ${colors.border}` }}>
            <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap", fontSize: "13px" }}>
                <strong title={info?.tip || ""} style={{ cursor: info ? "help" : "default" }}>{label}</strong>
                <span>Fail <strong style={{ color: counts.fail ? "#721c24" : undefined }}>{counts.fail || 0}</strong></span>
                <span>Warn <strong style={{ color: counts.warn ? "#856404" : undefined }}>{counts.warn || 0}</strong></span>
                <span>Pass <strong>{counts.pass || 0}</strong></span>
            </div>
            {info?.tip && <div style={{ fontSize: "11px", color: colors.gray600 }}>{info.tip}</div>}

            {fails.length > 0 && <RowsTable rows={fails} />}

            {warns.length > 0 && (
                <div style={{ fontSize: "12px", marginTop: "4px" }}>
                    <span style={{ color: "#856404" }}>{label}: {warns.length} {warns.length === 1 ? "token" : "tokens"}</span>{" "}
                    <button type="button" style={linkButton} onClick={() => setShowWarns(!showWarns)} aria-expanded={showWarns}>
                        {showWarns ? "Hide" : "Show"}
                    </button>
                    {showWarns && (
                        <div style={{ marginTop: "4px" }}>
                            <input
                                type="search"
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                placeholder="Filter by symbol or token id"
                                aria-label={`Filter ${label} by symbol or token id`}
                                style={{ fontSize: "12px", padding: "3px 6px", border: `1px solid ${colors.border}`, borderRadius: "4px", width: "240px", maxWidth: "100%" }}
                            />
                            <RowsTable rows={shownWarns} />
                            {!term && !showAll && filteredWarns.length > WARN_PREVIEW && (
                                <button type="button" style={linkButton} onClick={() => setShowAll(true)}>
                                    Show all {filteredWarns.length}
                                </button>
                            )}
                            {term && filteredWarns.length === 0 && <div style={{ color: colors.gray600 }}>No match.</div>}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

/** Counts per check from the run summary, plus any check that only shows up in the rows. */
const groupChecks = (run, checks) => {
    const byCheck = { ...(run?.summary?.byCheck || {}) };
    checks.forEach((c) => {
        if (!byCheck[c.check]) byCheck[c.check] = { pass: 0, warn: 0, fail: 0 };
    });
    const rank = (counts) => (counts.fail ? STATUS_ORDER.fail : counts.warn ? STATUS_ORDER.warn : STATUS_ORDER.pass);
    return Object.entries(byCheck)
        .map(([code, counts]) => ({ code, counts, rows: checks.filter((c) => c.check === code) }))
        .sort((a, b) => rank(a.counts) - rank(b.counts) || (CHECK_INFO[a.code]?.label || a.code).localeCompare(CHECK_INFO[b.code]?.label || b.code));
};

const ReconciliationPanel = () => {
    const [run, setRun] = useState(null);
    const [checks, setChecks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(false);
    const [message, setMessage] = useState(null);
    const pollTimer = useRef(null);

    const load = useCallback(async () => {
        try {
            const res = await getRequest(API_ENDPOINTS.RECONCILIATION);
            if (!res?.success) throw new Error(res?.error || "Could not load the reconciliation.");
            setRun(res.data?.run || null);
            setChecks(res.data?.checks || []);
            return res.data?.run || null;
        } catch (e) {
            setMessage({ ok: false, text: e.message || "Could not load the reconciliation." });
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        return () => clearTimeout(pollTimer.current);
    }, [load]);

    // After a run that timed out or is still going, check the latest run until
    // it is a different one that has finished, or until we give up.
    const pollUntilDone = useCallback(
        (previousId, startedAt) => {
            pollTimer.current = setTimeout(async () => {
                const latest = await load();
                const done = latest && latest.id !== previousId && latest.status !== "running";
                if (done) {
                    setRunning(false);
                    setMessage({ ok: true, text: "Reconciliation finished." });
                } else if (Date.now() - startedAt > POLL_FOR_MS) {
                    setRunning(false);
                    setMessage({ ok: false, text: "Still no result after 3 minutes. Refresh later to see it." });
                } else {
                    pollUntilDone(previousId, startedAt);
                }
            }, POLL_EVERY_MS);
        },
        [load],
    );

    const runNow = async () => {
        const previousId = run?.id;
        setRunning(true);
        setMessage({ ok: true, text: "Running. This can take about a minute." });
        try {
            const res = await postRequest(API_ENDPOINTS.RUN_RECONCILIATION, {});
            if (res?.success && res.data && res.data.status === "running") {
                pollUntilDone(previousId, Date.now());
            } else if (res?.success && res.data) {
                await load();
                setRunning(false);
                setMessage({ ok: true, text: "Reconciliation finished." });
            } else if (res?.success) {
                setMessage({ ok: true, text: "Another run is already going. Waiting for it to finish." });
                pollUntilDone(previousId, Date.now());
            } else {
                setMessage({ ok: false, text: `${res?.error || "The run failed."} Checking again.` });
                pollUntilDone(previousId, Date.now());
            }
        } catch (e) {
            // A gateway timeout lands here while lp-api keeps running; keep checking.
            setMessage({ ok: true, text: "Still running. Checking for the result." });
            pollUntilDone(previousId, Date.now());
        }
    };

    const counts = run?.summary?.counts || {};
    const groups = run ? groupChecks(run, checks) : [];

    return (
        <div style={cardStyle}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap", marginBottom: "12px" }}>
                <h3 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: colors.primary }}>Ledger reconciliation</h3>
                <button type="button" onClick={runNow} disabled={running || loading} style={buttonStyle(running || loading)}>
                    {running ? "Running…" : "Run now"}
                </button>
            </div>
            <p style={{ margin: "0 0 12px", fontSize: "13px", color: colors.gray600 }}>
                The pool ledger checked against the Hedera mirror node, every night at 02:00 UTC.
            </p>

            {message && (
                <div role="status" style={{ marginBottom: "12px", fontSize: "13px", color: message.ok ? colors.gray600 : "#721c24" }}>
                    {message.text}
                </div>
            )}

            {loading ? (
                <p style={{ fontSize: "13px", color: colors.gray600 }}>Loading…</p>
            ) : !run ? (
                <p style={{ fontSize: "13px", color: colors.gray600 }}>No run yet.</p>
            ) : (
                <>
                    <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "center", fontSize: "13px", marginBottom: "12px" }}>
                        <span><Badge status={run.status} /></span>
                        <span>Finished: <strong>{formatTime(run.finishedAt || run.startedAt)}</strong></span>
                        <span>Trigger: <strong>{run.trigger}</strong></span>
                        <span>Pass <strong>{counts.pass ?? 0}</strong> · Warn <strong>{counts.warn ?? 0}</strong> · Fail <strong>{counts.fail ?? 0}</strong></span>
                    </div>
                    {run.error && <p style={{ fontSize: "13px", color: "#721c24" }}>Error: {run.error}</p>}

                    <div style={{ maxHeight: "420px", overflowY: "auto", border: `1px solid ${colors.border}`, borderRadius: "6px", padding: "4px 12px" }}>
                        {groups.length === 0 ? (
                            <p style={{ fontSize: "13px", color: colors.gray600, margin: "6px 0" }}>No checks in this run.</p>
                        ) : (
                            groups.map((g) => <CheckGroup key={g.code} code={g.code} counts={g.counts} rows={g.rows} />)
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default ReconciliationPanel;
